/**
 * Shared track interpolation utility.
 * Used by boat layers, track layers, and overlays so boat, track, and bad air stay in sync.
 */

export type GetTimestamp = (d: any) => number | Date;
export type GetNumber = (d: any) => number | undefined;

function toMs(ts: number | Date): number {
  if (typeof ts === 'number') return ts;
  return ts.getTime();
}

/** Interpolate angle (0-360) with wrapping */
export function interpolateAngle(angle1: number, angle2: number, factor: number): number {
  let diff = angle2 - angle1;
  if (diff > 180) diff -= 360;
  if (diff < -180) diff += 360;
  const interpolated = angle1 + diff * factor;
  return ((interpolated % 360) + 360) % 360;
}

/** Normalize degrees to (-180, 180] for SVG rotate / screen space. */
export function normalizeRotationDeg(deg: number): number {
  if (!Number.isFinite(deg)) return 0;
  let r = deg % 360;
  if (r > 180) r -= 360;
  if (r <= -180) r += 360;
  return r;
}

/** Shortest-path rotation in degrees (e.g. 350° → 10° steps through 0°, not 180°). */
export function shortestPathRotation(currentDeg: number, targetDeg: number): number {
  let diff = targetDeg - currentDeg;
  if (diff > 180) diff -= 360;
  if (diff < -180) diff += 360;
  return currentDeg + diff;
}

/** Heading (0–360°) → screen rotation with shortest arc from previous heading. */
export function smoothHeadingDeg(previousHdg: number | undefined, nextHdg: number): number {
  const normalized = ((nextHdg % 360) + 360) % 360;
  if (previousHdg == null || !Number.isFinite(previousHdg)) return normalized;
  return interpolateAngle(previousHdg, normalized, 1);
}

/** Parse rotate(deg) from an SVG transform string; returns 0 if missing. */
export function parseRotateDegFromTransform(transform: string | null | undefined): number {
  if (!transform) return 0;
  const match = transform.match(/rotate\(([^)]+)\)/);
  const n = match ? parseFloat(match[1]) : NaN;
  return Number.isFinite(n) ? n : 0;
}

/** Write interpolated heading to common live/dataset field names. */
export function assignInterpolatedHeading(target: Record<string, unknown>, hdgDeg: number): void {
  target.Hdg = hdgDeg;
  target.Hdg_deg = hdgDeg;
  target.hdg_deg = hdgDeg;
  target.hdg = hdgDeg;
}

/** Bearing in degrees from (fromLat, fromLon) to (toLat, toLon), 0–360. */
export function bearingDeg(
  fromLat: number,
  fromLon: number,
  toLat: number,
  toLon: number
): number {
  const toRad = (x: number) => (x * Math.PI) / 180;
  const dLon = toRad(toLon - fromLon);
  const y = Math.sin(dLon) * Math.cos(toRad(toLat));
  const x =
    Math.cos(toRad(fromLat)) * Math.sin(toRad(toLat)) -
    Math.sin(toRad(fromLat)) * Math.cos(toRad(toLat)) * Math.cos(dLon);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

/** Course-over-ground style heading from a lat/lng segment; undefined when movement is negligible. */
export function motionHeadingDeg(
  fromLat: number,
  fromLon: number,
  toLat: number,
  toLon: number
): number | undefined {
  if (![fromLat, fromLon, toLat, toLon].every(Number.isFinite)) return undefined;
  if (Math.abs(toLat - fromLat) < 1e-9 && Math.abs(toLon - fromLon) < 1e-9) return undefined;
  return bearingDeg(fromLat, fromLon, toLat, toLon);
}

export interface InterpolateOptions {
  getTimestamp: GetTimestamp;
  getLat: GetNumber;
  getLng: GetNumber;
  getHdg?: GetNumber;
  getTwd?: GetNumber;
  getTws?: GetNumber;
  getTwa?: GetNumber;
  getBsp?: GetNumber;
  twaName?: string;
  /** When true, skip copy+sort (catalog is already sorted by timestamp ascending). */
  assumeSorted?: boolean;
  /** When true, return the last sample instead of extrapolating past it (live replay data edge). */
  noExtrapolate?: boolean;
}

/** First index i in sorted (ascending by getTs) with getTs(sorted[i]) >= targetMs, or sorted.length if none. */
export function lowerBoundByTimeAscending(sorted: unknown[], targetMs: number, getTs: (d: unknown) => number): number {
  let lo = 0;
  let hi = sorted.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (getTs(sorted[mid]) < targetMs) {lo = mid + 1;}
    else {hi = mid;}
  }
  return lo;
}

/**
 * Like {@link getInterpolatedPointAtTime} but `sorted` must be sorted ascending by `options.getTimestamp`.
 * O(log n) per call vs O(n log n) when sorting inside the hot path (e.g. many map markers).
 */
export function getInterpolatedPointAtTimeFromSortedTrack(
  sorted: unknown[],
  targetTime: Date,
  options: InterpolateOptions
): unknown | null {
  if (!sorted || sorted.length === 0 || !targetTime) {return null;}
  const targetMs = targetTime.getTime();
  const { getTimestamp, getLat, getLng, getHdg, getTwd, getTws, getTwa, getBsp, twaName } = options;
  const getTs = (d: unknown) => toMs(getTimestamp(d));

  const nextIdx = lowerBoundByTimeAscending(sorted, targetMs, getTs);
  const next = nextIdx < sorted.length ? sorted[nextIdx] : null;
  let prev: unknown = null;
  if (nextIdx < sorted.length && getTs(sorted[nextIdx]) === targetMs) {
    prev = sorted[nextIdx];
  } else if (nextIdx > 0) {
    prev = sorted[nextIdx - 1];
  }

  if (prev && next && prev !== next) {
    const t1 = getTs(prev);
    const t2 = getTs(next);
    if (t1 < t2 && targetMs > t1 && targetMs < t2) {
      const factor = (targetMs - t1) / (t2 - t1);
      const lat1 = getLat(prev);
      const lng1 = getLng(prev);
      const lat2 = getLat(next);
      const lng2 = getLng(next);
      if (lat1 === undefined || lng1 === undefined || lat2 === undefined || lng2 === undefined) {
        return targetMs - t1 < t2 - targetMs ? prev : next;
      }
      const twd1 = getTwd?.(prev) ?? (prev as Record<string, unknown>).Twd ?? (prev as Record<string, unknown>).twd ?? 0;
      const twd2 = getTwd?.(next) ?? (next as Record<string, unknown>).Twd ?? (next as Record<string, unknown>).twd ?? 0;
      const tws1 = getTws?.(prev) ?? (prev as Record<string, unknown>).Tws ?? (prev as Record<string, unknown>).tws ?? 0;
      const tws2 = getTws?.(next) ?? (next as Record<string, unknown>).Tws ?? (next as Record<string, unknown>).tws ?? 0;
      const h1 = getHdg?.(prev);
      const h2 = getHdg?.(next);
      const twa1 = getTwa?.(prev) ?? (twaName && (prev as Record<string, unknown>)[twaName]) ?? (prev as Record<string, unknown>).Twa ?? (prev as Record<string, unknown>).twa ?? 0;
      const twa2 = getTwa?.(next) ?? (twaName && (next as Record<string, unknown>)[twaName]) ?? (next as Record<string, unknown>).Twa ?? (next as Record<string, unknown>).twa ?? 0;
      const bsp1 = getBsp?.(prev) ?? (prev as Record<string, unknown>).Bsp ?? (prev as Record<string, unknown>).bsp ?? 0;
      const bsp2 = getBsp?.(next) ?? (next as Record<string, unknown>).Bsp ?? (next as Record<string, unknown>).bsp ?? 0;

      const lat = lat1 + (lat2 - lat1) * factor;
      const lng = lng1 + (lng2 - lng1) * factor;
      const hdgInterp = h1 !== undefined && h2 !== undefined ? interpolateAngle(h1, h2, factor) : (h1 ?? h2 ?? 0);
      const interpolated: Record<string, unknown> = {
        ...(prev as Record<string, unknown>),
        Datetime: targetTime,
        timestamp: targetMs,
        Lat: lat,
        Lng: lng,
        Lat_dd: lat,
        Lng_dd: lng,
        lat_dd: lat,
        lng_dd: lng,
        Twd: interpolateAngle(Number(twd1), Number(twd2), factor),
        Tws: Number(tws1) + (Number(tws2) - Number(tws1)) * factor,
        Twa: interpolateAngle(Number(twa1), Number(twa2), factor),
        Bsp: Number(bsp1) + (Number(bsp2) - Number(bsp1)) * factor,
        Hdg: hdgInterp,
      };
      assignInterpolatedHeading(interpolated, hdgInterp);
      return interpolated;
    }
  }

  return prev ?? next ?? sorted[0] ?? null;
}

/**
 * Find bracketing points for targetTime and return interpolated point, or nearest if no bracket.
 */
export function getInterpolatedPointAtTime(
  data: any[],
  targetTime: Date,
  options: InterpolateOptions
): any | null {
  if (!data || data.length === 0 || !targetTime) return null;
  const targetMs = targetTime.getTime();
  const { getTimestamp, getLat, getLng, getHdg, getTwd, getTws, getTwa, getBsp, twaName } = options;
  const getTs = (d: any) => toMs(getTimestamp(d));

  const sorted = options.assumeSorted ? data : [...data].sort((a, b) => getTs(a) - getTs(b));
  let prev: any = null;
  let next: any = null;
  for (let i = 0; i < sorted.length; i++) {
    const t = getTs(sorted[i]);
    if (t <= targetMs) prev = sorted[i];
    if (t >= targetMs) {
      next = sorted[i];
      break;
    }
  }

  if (prev && next && prev !== next) {
    const t1 = getTs(prev);
    const t2 = getTs(next);
    if (t1 < t2 && targetMs > t1 && targetMs < t2) {
      const factor = (targetMs - t1) / (t2 - t1);
      const lat1 = getLat(prev);
      const lng1 = getLng(prev);
      const lat2 = getLat(next);
      const lng2 = getLng(next);
      if (lat1 === undefined || lng1 === undefined || lat2 === undefined || lng2 === undefined) {
        return targetMs - t1 < t2 - targetMs ? prev : next;
      }
      const twd1 = getTwd?.(prev) ?? (prev as any).Twd ?? (prev as any).twd ?? 0;
      const twd2 = getTwd?.(next) ?? (next as any).Twd ?? (next as any).twd ?? 0;
      const tws1 = getTws?.(prev) ?? (prev as any).Tws ?? (prev as any).tws ?? 0;
      const tws2 = getTws?.(next) ?? (next as any).Tws ?? (next as any).tws ?? 0;
      const h1 = getHdg?.(prev);
      const h2 = getHdg?.(next);
      const twa1 = getTwa?.(prev) ?? (twaName && (prev as any)[twaName]) ?? (prev as any).Twa ?? (prev as any).twa ?? 0;
      const twa2 = getTwa?.(next) ?? (twaName && (next as any)[twaName]) ?? (next as any).Twa ?? (next as any).twa ?? 0;
      const bsp1 = getBsp?.(prev) ?? (prev as any).Bsp_kph ?? (prev as any).Bsp ?? (prev as any).bsp;
      const bsp2 = getBsp?.(next) ?? (next as any).Bsp_kph ?? (next as any).Bsp ?? (next as any).bsp;
      const bsp1n = bsp1 != null && Number.isFinite(Number(bsp1)) ? Number(bsp1) : undefined;
      const bsp2n = bsp2 != null && Number.isFinite(Number(bsp2)) ? Number(bsp2) : undefined;
      const bspInterp =
        bsp1n !== undefined && bsp2n !== undefined
          ? bsp1n + (bsp2n - bsp1n) * factor
          : bsp1n ?? bsp2n;

      const bracketMotion = motionHeadingDeg(lat1, lng1, lat2, lng2);
      const hdgInterp =
        h1 !== undefined && h2 !== undefined
          ? interpolateAngle(h1, h2, factor)
          : (h1 ?? h2 ?? bracketMotion ?? 0);
      const lat = lat1 + (lat2 - lat1) * factor;
      const lng = lng1 + (lng2 - lng1) * factor;
      const interpolated: any = {
        ...prev,
        Datetime: targetTime,
        timestamp: targetMs,
        Lat: lat,
        Lng: lng,
        Lat_dd: lat,
        Lng_dd: lng,
        lat_dd: lat,
        lng_dd: lng,
        Twd: interpolateAngle(twd1, twd2, factor),
        Tws: tws1 + (tws2 - tws1) * factor,
        Twa: interpolateAngle(twa1, twa2, factor),
        Bsp: bspInterp ?? bsp1n ?? bsp2n ?? 0,
        Hdg: hdgInterp,
        Grade: (prev as any).Grade != null && (next as any).Grade != null
          ? (prev as any).Grade + ((next as any).Grade - (prev as any).Grade) * factor
          : (prev as any).Grade ?? (next as any).Grade,
        Vmg_perc: (prev as any).Vmg_perc != null && (next as any).Vmg_perc != null
          ? (prev as any).Vmg_perc + ((next as any).Vmg_perc - (prev as any).Vmg_perc) * factor
          : (prev as any).Vmg_perc ?? (next as any).Vmg_perc,
      };
      assignInterpolatedHeading(interpolated, hdgInterp);
      if (bspInterp != null && Number.isFinite(bspInterp)) {
        interpolated.Bsp_kph = bspInterp;
      }
      return interpolated;
    }
    if (t1 === t2 && targetMs === t1) {
      return prev;
    }
  }

  // Extrapolate when playhead is after the last sample (live replay window end vs buffered WS edge)
  if (
    !options.noExtrapolate &&
    sorted.length >= 2 &&
    targetMs > getTs(sorted[sorted.length - 1])
  ) {
    const beforeLast = sorted[sorted.length - 2];
    const last = sorted[sorted.length - 1];
    const t1 = getTs(beforeLast);
    const t2 = getTs(last);
    if (t1 < t2) {
      const factor = (targetMs - t2) / (t2 - t1);
      const lat1 = getLat(beforeLast);
      const lng1 = getLng(beforeLast);
      const lat2 = getLat(last);
      const lng2 = getLng(last);
      if (lat1 !== undefined && lng1 !== undefined && lat2 !== undefined && lng2 !== undefined) {
        const extLat = lat2 + (lat2 - lat1) * factor;
        const extLng = lng2 + (lng2 - lng1) * factor;
        const h2 = getHdg?.(last);
        const h1 = getHdg?.(beforeLast);
        const motionHdg = motionHeadingDeg(lat1, lng1, extLat, extLng);
        const hdgInterp = motionHdg ?? h2 ?? h1 ?? 0;
        const extrapolated: any = {
          ...last,
          Datetime: targetTime,
          timestamp: targetMs,
          Lat: extLat,
          Lng: extLng,
          Lat_dd: extLat,
          Lng_dd: extLng,
          Hdg: hdgInterp,
        };
        assignInterpolatedHeading(extrapolated, hdgInterp);
        return extrapolated;
      }
    }
  }

  const p = prev ?? next ?? sorted[0];
  return p ?? null;
}

/**
 * Split a track polyline at time gaps. The last point (playhead extension) always
 * stays connected to the previous point even when its timestamp is far ahead.
 */
export function splitTrackPolylineAtGaps<T>(
  points: T[],
  getTimestampMs: (p: T) => number,
  gapThresholdMs: number
): T[][] {
  if (points.length === 0) return [];
  const segments: T[][] = [];
  let current: T[] = [];
  for (let i = 0; i < points.length; i++) {
    const point = points[i];
    if (i === 0) {
      current.push(point);
      continue;
    }
    const gap = getTimestampMs(point) - getTimestampMs(points[i - 1]);
    const isPlayheadTail = i === points.length - 1;
    if (!isPlayheadTail && gap > gapThresholdMs) {
      if (current.length > 1) segments.push([...current]);
      current = [point];
    } else {
      current.push(point);
    }
  }
  if (current.length > 1) segments.push(current);
  return segments;
}

/**
 * Get interpolated point per source. Data can be mixed sources; sourceKey(d) returns source id.
 */
export function getInterpolatedPointAtTimePerSource(
  data: any[],
  targetTime: Date,
  sourceKey: (d: any) => number | string | undefined,
  options: InterpolateOptions
): Map<number | string, any> {
  const result = new Map<number | string, any>();
  const bySource = new Map<number | string, any[]>();
  for (const d of data) {
    const key = sourceKey(d);
    if (key === undefined || key === null) continue;
    if (!bySource.has(key)) bySource.set(key, []);
    bySource.get(key)!.push(d);
  }
  for (const [key, points] of bySource) {
    const pt = getInterpolatedPointAtTime(points, targetTime, options);
    if (pt) result.set(key, pt);
  }
  return result;
}
